import { Injectable } from '@angular/core';
import type { GetPcapPlayerFilesQuery, PlayPcapsMutationVariables } from '@uigy/generated/graphql';
import { PcapPlayerIndicatorService } from '@uigy/modules/settings/modules/pcap-player/components/pcap-player-indicator/pcap-player-indicator.service';
import { PlayPcapFormComponent } from '@uigy/modules/settings/modules/pcap-player/components/play-pcap-form/play-pcap-form.component';
import { PcapPlayerService } from '@uigy/modules/settings/modules/pcap-player/services/pcap-player.service';
import { SliderSizeEnumV2 } from '@uigy/shared/components/slider-v2/enums/slider-v2.enum';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { WizardPage } from '@uigy/shared/components/wizard/models/wizard-page';
import { WizardConfig } from '@uigy/shared/components/wizard/models/wizard.interfaces';
import { WizardService } from '@uigy/shared/components/wizard/services/wizard.service';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { ActionMenuItem } from '@uigy/shared/modules/grid/models/grid';
import { JobsV2Service } from '@uigy/shared/services/jobs-v2/jobs-v2.service';
import { LanguageService } from '@uigy/shared/services/language.service';
import { PopupService } from '@uigy/shared/services/popup.service';
import { UploadFileService } from '@uigy/shared/services/upload-file/upload-file.service';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subject } from 'rxjs';
import { concatMap, finalize, map, switchMap, take, tap } from 'rxjs/operators';


@Injectable()
export class PcapPlayerGridPageService {
  public exportCsv$ = new Subject<string>();
  private _isRunningFlag = new BehaviorSubject(false);
  private _PCAPFilesList$ = new BehaviorSubject<GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number][]>([]);
  private _pcapPlayerText = this.languageService.texts.settings.env_config.pcap_player;

  get PCAPFilesList$(): Observable<GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number][]> {
    return this._PCAPFilesList$.asObservable();
  }

  set PCAPFilesListSetter$(value: GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number][]) {
    this._PCAPFilesList$.next(value);
  }

  get isGridActionRunning$(): Observable<boolean> {
    return this._isRunningFlag.asObservable();
  }

  get pcapsActionStatus(): Observable<boolean> {
    return combineLatest([
      this.pcapPlayerIndicator.isPlaying$,
      this._isRunningFlag.asObservable()
    ]).pipe(
      map(([systemActionFlag, currentUserActionFlag]: [boolean, boolean]) => currentUserActionFlag || systemActionFlag)
    );
  }

  constructor(private languageService: LanguageService,
              private pcapPlayerService: PcapPlayerService,
              private jobsV2Service: JobsV2Service,
              private toasterService: ToasterService,
              private popupService: PopupService,
              private pcapPlayerIndicator: PcapPlayerIndicatorService,
              private wizardService: WizardService) {
  }


  public async fetchPcapFiles(): Promise<void> {
    this.PCAPFilesListSetter$ = await firstValueFrom(this.pcapPlayerService.fetchPcapFilesDetails()).catch(() => []);
  }

  public openPlayPcapSlider(pcaps: GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number][]): void {
    const sliderConfig = this.getSliderConfig(pcaps);
    this.wizardService.openWizardSlider<GetPcapPlayerFilesQuery, PlayPcapsMutationVariables>(sliderConfig).pipe(
      tap((data: PlayPcapsMutationVariables) => {
        this.notifyAboutPlayingPCAP(pcaps[0].name, data.speed!);
      }),
      switchMap((data: PlayPcapsMutationVariables) => {
        const messageOptions = {
          name: pcaps[0].name,
          speed: data.speed
        };
        return this.pcapPlayerService.playPcap(data).pipe(
          this.jobsV2Service.handleJobOperator(),
          this.jobsV2Service.notificationsOperator({
            singleSuccessMessage: this.languageService.getTranslationTyped('settings.env_config.pcap_player.play.messages.success', messageOptions),
            singleFailureMessage: this.languageService.getTranslationTyped('settings.env_config.pcap_player.play.messages.failure', messageOptions)
          }),
          take(1),
          tap(async () => await this.fetchPcapFiles()),
        );
      })
    ).subscribe();
  }

  public uploadPcap(formData: FormData): void {
    const file = <File | null>formData.get('file');
    if (file) {
      try {
        UploadFileService.readFile('uploadedFile', file).pipe(
          tap(() =>  this._isRunningFlag.next(true)),
          concatMap((form: FormData) => this.pcapPlayerService.uploadPcap(form).pipe(
            this.jobsV2Service.handleJobOperator(),
            this.jobsV2Service.simpleNotificationsOperator({
              success: this._pcapPlayerText.upload.messages.success
            }),
            finalize(async () => {
              this._isRunningFlag.next(false);
              await this.fetchPcapFiles();
            }))
          )
        ).subscribe();
      } catch (e) {
        this.toasterService.openToaster({
          message: this._pcapPlayerText.upload.messages.failure,
          type: ToasterType.Error
        });
        console.error('upload pcap file failed,', e);
      }
    }
  }

  public getActionItems(currentFocusedItem?: GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number], disableActions: boolean = true): ActionMenuItem[] {
    return [
      {
        name: this._pcapPlayerText.actions.delete,
        action: () => currentFocusedItem ? this.openDeletePCAPFileWarning(currentFocusedItem.id) : void (0),
        id: AppIdsEnum.pcap_btn_action_del,
      },
      {
        name: this._pcapPlayerText.actions.play,
        action: () => currentFocusedItem ? this.openPlayPcapSlider([currentFocusedItem]) : void (0),
        disabled: !currentFocusedItem || disableActions,
        id: AppIdsEnum.pcap_btn_action_play,
      }
    ];
  }

  private getSliderConfig(pcaps: GetPcapPlayerFilesQuery['pcapPlayer']['files']['nodes'][number][]): WizardConfig<GetPcapPlayerFilesQuery> {
    return {
      submitBtnText: this._pcapPlayerText.play.slider.submitBtnText,
      pages: [new WizardPage(PlayPcapFormComponent)],
      header: this._pcapPlayerText.play.slider.header,
      overlay: SliderSizeEnumV2.form,
      data: { pcapPlayer: {files : { nodes: pcaps }}}
    };
  }

  private notifyAboutPlayingPCAP(name: string, speed: number): void {
    this.toasterService.openToaster({
      type: ToasterType.Info, message: this.languageService.getTranslationTyped('settings.env_config.pcap_player.play.start_playing_message', {name, speed})
    });
  }

  private openDeletePCAPFileWarning(pcapId: string): void {
    this.popupService.openPopup({
      header: this._pcapPlayerText.delete.popup.header,
      text: this._pcapPlayerText.delete.popup.text,
      submit: {
        title: this._pcapPlayerText.delete.popup.submit.title,
        execute: () => this.pcapPlayerService.deletePcapFile({id: pcapId}).pipe(
          take(1),
          tap(() => this.toasterService.openToaster({
            message: this._pcapPlayerText.delete.messages.success,
          type: ToasterType.Success}),
            ),
          tap(() => this.fetchPcapFiles()),
        ).subscribe(),
      }
    });
  }
}
